const base = "";

const MAX_WIDTH = 1600;
const MAX_HEIGHT = 1600;
const TARGET_SIZE_KB = 200;
const TARGET_BYTES = TARGET_SIZE_KB * 1024;

async function compressImageFile(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  if (file.size <= TARGET_BYTES) return file;

  const image = await loadImage(file);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;

  let { width, height } = image;
  if (width > MAX_WIDTH || height > MAX_HEIGHT) {
    const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height, 1);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  canvas.width = width;
  canvas.height = height;
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image, 0, 0, width, height);

  let quality = 0.92;
  let outputType = file.type === "image/png" ? "image/jpeg" : file.type;
  if (file.type === "image/gif" || file.type === "image/webp") outputType = file.type;

  let blob: Blob | null = await new Promise((res) => canvas.toBlob(res, outputType, quality));
  if (!blob) return file;

  while (blob.size > TARGET_BYTES && quality > 0.3) {
    quality -= 0.08;
    blob = await new Promise((res) => canvas.toBlob(res, outputType, quality));
    if (!blob) return file;
  }

  if (blob.size > TARGET_BYTES && (width > 800 || height > 800)) {
    const scale = Math.min(800 / width, 800 / height, 0.85);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    canvas.width = width;
    canvas.height = height;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);
    quality = 0.85;
    blob = await new Promise((res) => canvas.toBlob(res, outputType, quality));
    if (!blob) return file;
    while (blob.size > TARGET_BYTES && quality > 0.3) {
      quality -= 0.08;
      blob = await new Promise((res) => canvas.toBlob(res, outputType, quality));
      if (!blob) return file;
    }
  }

  const extension = outputType === "image/jpeg" ? "jpg" : outputType.split("/")[1];
  const name = file.name.replace(/\.[^.]+$/, "") + "-optimized." + extension;
  return new File([blob], name, { type: outputType });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

export const api = {
  content: {
    get: async (section: string) => {
      const r = await fetch(`${base}/api/content?section=${section}`);
      const j = await r.json() as any;
      return j.content ?? null;
    },
    set: async (section: string, content: object) => {
      await fetch(`${base}/api/content`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ section, content }),
      });
    },
  },
  settings: {
    get: async () => {
      const r = await fetch(`${base}/api/settings`);
      const j = await r.json() as any;
      return j.settings ?? {};
    },
    set: async (data: object) => {
      await fetch(`${base}/api/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    },
  },
  contact: {
    send: async (data: { name: string; email?: string; phone?: string; message: string }) => {
      const r = await fetch(`${base}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return r.ok;
    },
  },
  messages: {
    list: async () => {
      const r = await fetch(`${base}/api/messages`);
      const j = await r.json() as any;
      return j.messages ?? [];
    },
    delete: async (id: string) => {
      await fetch(`${base}/api/messages`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
    },
  },
  registrations: {
    list: async () => {
      const r = await fetch(`${base}/api/registrations`);
      const j = await r.json() as any;
      return j.registrations ?? [];
    },
    create: async (data: object) => {
      const r = await fetch(`${base}/api/registrations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      return r.ok;
    },
    delete: async (id: string) => {
      await fetch(`${base}/api/registrations`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
    },
  },
  gallery: {
    list: async () => {
      const r = await fetch(`${base}/api/gallery`);
      const j = await r.json() as any;
      return { images: j.images ?? [], albums: j.albums ?? [] };
    },
    add: async (url: string, caption?: string, sort_order?: number, album?: string) => {
      const r = await fetch(`${base}/api/gallery`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, caption, sort_order, album }),
      });
      const j = await r.json() as any;
      return j.ok ? j.id : null;
    },
    update: async (id: number, caption: string, sort_order: number, album: string) => {
      await fetch(`${base}/api/gallery`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, caption, sort_order, album }),
      });
    },
    delete: async (id: number) => {
      await fetch(`${base}/api/gallery`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
    },
  },
  upload: async (file: File, compress: boolean = true): Promise<string | null> => {
    const finalFile = compress ? await compressImageFile(file) : file;
    const r = await fetch(`${base}/api/upload`, {
      method: "POST",
      headers: { "X-File-Type": finalFile.type || "image/jpeg" },
      body: finalFile,
    });
    const j = await r.json() as any;
    return j.ok ? j.url : null;
  },
};
